import { useMemo } from 'react';
import { useMatchedGuestProfilesById } from '../queries';
import { useMatchedGuestProfiles } from '../queries/useMatchedGuestProfiles';
import { useNextMatchedGuestProfiles } from '../queries/useNextMatchedGuestProfiles';
import { MergeMode } from '../types';

interface UseMatchedProfilesParams {
    cursor?: string;
    guestProfileIds: string[];
    mergeMode: MergeMode;
    newMatchQueryEnabled: boolean;
    previousMatchId?: string;
    totalProfileMatches: number;
}

export const useMatchedProfiles = ({
    cursor,
    guestProfileIds,
    mergeMode,
    newMatchQueryEnabled,
    previousMatchId,
    totalProfileMatches,
}: UseMatchedProfilesParams) => {
    const { data: matchedProfilesWithPagination, refetch } = useMatchedGuestProfiles({
        cursor: cursor || null,
        enabled: newMatchQueryEnabled ? false : mergeMode === MergeMode.Queue,
    });

    const { data: matchedGuestProfilesNew, isFetching: nextMatchedFetching } = useNextMatchedGuestProfiles(
        newMatchQueryEnabled && mergeMode === MergeMode.Queue,
        previousMatchId || null
    );

    const { data: matchedGuestProfiles, isFetching: matchedByIdFetching } = useMatchedGuestProfilesById(
        mergeMode === MergeMode.Single ? guestProfileIds : matchedProfilesWithPagination?.guestIds || [],
        mergeMode === MergeMode.Single ||
            (!newMatchQueryEnabled && (mergeMode === MergeMode.Queue ? totalProfileMatches > 0 : true))
    );

    const profiles = useMemo(() => {
        if (mergeMode === MergeMode.Queue && newMatchQueryEnabled) {
            return matchedGuestProfilesNew;
        } else if (mergeMode === MergeMode.Single || !newMatchQueryEnabled) {
            return matchedGuestProfiles;
        }
        return null;
    }, [mergeMode, newMatchQueryEnabled, matchedGuestProfilesNew, matchedGuestProfiles]);

    return {
        profiles,
        loading: matchedByIdFetching || nextMatchedFetching,
        matchedProfilesWithPagination,
        refetch,
    };
};
